package de.xtl.retroguide.question.scenes

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.spring
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import de.xtl.retroguide.protocol.MAX_ROTI
import de.xtl.retroguide.protocol.MIN_ROTI
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

private val Zones = listOf(
    Color(0xFFE53935),
    Color(0xFFFB8C00),
    Color(0xFFFDD835),
    Color(0xFF9CCC65),
    Color(0xFF43A047),
)

/** Half-circle speedometer from 1 (red) to 5 (green); the needle springs to the average. */
@Composable
internal fun RotiGauge(average: Double, modifier: Modifier = Modifier) {
    val needle = remember { Animatable(MIN_ROTI.toFloat()) }
    LaunchedEffect(average) { needle.animateTo(average.toFloat(), spring(dampingRatio = .35f, stiffness = 60f)) }
    val ink = MaterialTheme.colorScheme.onSurface
    Canvas(modifier.fillMaxWidth().aspectRatio(2.2f)) {
        val stroke = size.height * .14f
        val radius = minOf(size.width / 2, size.height) - stroke
        val center = Offset(size.width / 2, size.height - stroke * .4f)
        val sweep = 180f / Zones.size
        Zones.forEachIndexed { index, color ->
            drawArc(
                color,
                startAngle = 180f + index * sweep,
                sweepAngle = sweep - 1.5f,
                useCenter = false,
                topLeft = center - Offset(radius, radius),
                size = Size(radius * 2, radius * 2),
                style = Stroke(stroke, cap = StrokeCap.Butt),
            )
        }
        val share = ((needle.value - MIN_ROTI) / (MAX_ROTI - MIN_ROTI)).coerceIn(-.05f, 1.05f)
        val angle = (180f + share * 180f) * PI.toFloat() / 180f
        val tip = center + Offset(cos(angle), sin(angle)) * (radius - stroke * .2f)
        drawLine(ink, center, tip, strokeWidth = stroke * .22f, cap = StrokeCap.Round)
        drawCircle(ink, stroke * .35f, center)
    }
}
